/**
 * referencesYMap.js
 *
 * CRUD API for added references stored inside the unified Y.Map("referenceMarks").
 *
 * Structure inside the Y.Doc:
 *
 *   Y.Map("referenceMarks")
 *     └── [referenceMarkId]: Y.Map
 *           └── addedReferences: Y.Map
 *                 └── [localId]: Y.Map  ← one Y.Map per reference, primitives as entries
 *
 * Only the `addedReferences` sub-map is written by the client. The sibling keys
 * (`suggestedReferences`, `claims`, `_meta`) are written exclusively by the backend.
 *
 * Every client write is wrapped in a ydoc.transact() call so all field assignments
 * inside one logical operation are delivered to peers as a single Y.js update message.
 *
 * Hard-delete pattern: removeReferenceMark calls addedReferences.delete(localId) — no isActive
 * flag needed. Y.UndoManager (scoped to yReferenceMarks) restores the deleted entry
 * automatically on Ctrl+Z, atomically with any associated ProseMirror mark changes.
 */

import * as Y from 'yjs';

// ---------------------------------------------------------------------------
// Internal key used for the added-references sub-map
// ---------------------------------------------------------------------------

const ADDED_REFS_KEY = 'addedReferences';

// ---------------------------------------------------------------------------
// Local-id helpers (mirrors logic in src/components/segmentItem/utils.js and
// src/firebase/firestore/reports.js so the key is always the same string)
// ---------------------------------------------------------------------------

/**
 * Derive a stable localId from a reference-data object.
 * The id encodes docId:startSegIdx:startSegOff:endSegIdx:endSegOff.
 *
 * @param {Object} data
 * @returns {string|null}
 */
export function generateLocalId(data) {
    if (!data) return null;
    if (data.localId) return data.localId;

    const s = data.start || {};
    const e = data.end || {};
    const sIdx = s.segmentIndex != null ? s.segmentIndex : data.index;
    const sOff = s.segmentOffset != null ? s.segmentOffset : (data.offset ?? 0);
    const eIdx = e.segmentIndex != null ? e.segmentIndex : sIdx;
    const eOff = e.segmentOffset != null ? e.segmentOffset : sOff;

    if (data.docId != null && sIdx != null && sOff != null && eIdx != null && eOff != null) {
        return [data.docId, sIdx, sOff, eIdx, eOff].join(':');
    }
    return null;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Get or create the per-mark Y.Map container under yReferenceMarks.
 * Does NOT create addedReferences yet — only the mark-level map.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yReferenceMarks  Root Y.Map("referenceMarks")
 * @param {string}            referenceMarkId
 * @returns {import('yjs').Map}
 */
function getOrCreateMarkMap(ydoc, yReferenceMarks, referenceMarkId) {
    if (!yReferenceMarks.has(referenceMarkId)) {
        yReferenceMarks.set(referenceMarkId, new Y.Map());
    }
    return yReferenceMarks.get(referenceMarkId);
}

/**
 * Get or create the addedReferences sub-map for a mark.
 * Must be called inside a ydoc.transact() block.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string}            referenceMarkId
 * @returns {import('yjs').Map}
 */
function getOrCreateAddedRefs(ydoc, yReferenceMarks, referenceMarkId) {
    const markMap = getOrCreateMarkMap(ydoc, yReferenceMarks, referenceMarkId);
    if (!markMap.has(ADDED_REFS_KEY)) {
        markMap.set(ADDED_REFS_KEY, new Y.Map());
    }
    return markMap.get(ADDED_REFS_KEY);
}

// ---------------------------------------------------------------------------
// Public write API  (client-owned)
// ---------------------------------------------------------------------------

/**
 * Initialise a reference mark entry in Y.Map("referenceMarks") immediately on
 * mark creation, before any reference is added.
 *
 * This ensures the backend observes the new mark as soon as it is created via
 * the floating menu, even when the user has not yet dragged a source onto it.
 * The entry contains an empty `addedReferences` sub-map so the structure is
 * consistent with entries that have real references.
 *
 * Idempotent: if the mark already exists this is a no-op.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yReferenceMarks  Root Y.Map("referenceMarks")
 * @param {string}            referenceMarkId
 */
export function initializeReferenceMark(ydoc, yReferenceMarks, referenceMarkId) {
    if (!ydoc || !yReferenceMarks) throw new Error('[referencesYMap] ydoc and yReferenceMarks are required');
    if (!referenceMarkId) throw new Error('[referencesYMap] referenceMarkId is required');

    if (yReferenceMarks.has(referenceMarkId)) return; // already exists — no-op

    ydoc.transact(() => {
        getOrCreateAddedRefs(ydoc, yReferenceMarks, referenceMarkId);
    }, null); // null origin → tracked by Y.UndoManager → undoable with Ctrl+Z
}

/**
 * Add a reference to a reference mark's addedReferences.
 *
 * Idempotent: if the same localId already exists the call is a no-op.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yReferenceMarks  Root Y.Map("referenceMarks")
 * @param {string}            referenceMarkId
 * @param {Object}            referenceData  All reference fields; must contain
 *                                           enough info to derive a localId.
 * @returns {string} The localId of the (possibly newly created) reference.
 */
export function addReference(ydoc, yReferenceMarks, referenceMarkId, referenceData) {
    if (!ydoc || !yReferenceMarks) throw new Error('[referencesYMap] ydoc and yReferenceMarks are required');
    if (!referenceMarkId) throw new Error('[referencesYMap] referenceMarkId is required');

    const localId = referenceData.localId || generateLocalId(referenceData);
    if (!localId) throw new Error('[referencesYMap] Cannot derive localId from referenceData');

    ydoc.transact(() => {
        const addedRefs = getOrCreateAddedRefs(ydoc, yReferenceMarks, referenceMarkId);

        if (addedRefs.has(localId)) {
            // Already present — no-op (hard delete means a present entry is always active)
            return;
        }

        // Build a plain-value snapshot of the reference.
        // Y.Map values must be primitives or nested Y types; nested objects are
        // JSON-stringified to keep this restriction.
        const refMap = new Y.Map();

        const fields = {
            localId,
            referenceMarkId,
            docId: referenceData.docId ?? null,
            fileId: referenceData.fileId ?? null,
            reportId: referenceData.reportId ?? null,
            documentType: referenceData.documentType ?? null,
            docUrl: referenceData.docUrl ?? null,
            docTitle: referenceData.docTitle ?? null,
            docSource: referenceData.docSource ?? null,
            docSourceType: referenceData.docSourceType ?? null,
            sourceScore: referenceData.sourceScore ?? null,
            content: referenceData.content ?? null,
            originalContent: referenceData.originalContent ?? null,
            originalLanguage: referenceData.originalLanguage ?? null,
            segmentId: referenceData.segmentId ?? null,
            index: referenceData.index ?? null,
            offset: referenceData.offset ?? null,
            start: referenceData.start ? JSON.stringify(referenceData.start) : null,
            end: referenceData.end ? JSON.stringify(referenceData.end) : null,
            // Provenance tag for references written by an AI feature rather than manually
            // (e.g. 'forecast' — MINSKY-16818 ticket 01) so downstream features can identify
            // where an added reference came from. null for the default manual/drag-drop flow.
            source: referenceData.source ?? null,
            // MINSKY-17131: the citation's source document classification level (U/C/S/TS) at
            // the time it was added, so the margin rail / whole-doc view can flag above-(U)
            // citations without a lookup. References added before this field existed fall back
            // to a backfill lookup (see useDocumentClassificationCache). Some existing call
            // sites (segmentItem.js's actionIdentifiers, CitationSuggestionCard.jsx) already
            // carry this under the `classificationLevel` key — accept either.
            docClassificationLevel: referenceData.docClassificationLevel ?? referenceData.classificationLevel ?? null,
            evidenceScore: null,   // written by backend after verification
            createdAt: Date.now(),
        };

        Object.entries(fields).forEach(([k, v]) => {
            if (v !== undefined) refMap.set(k, v);
        });

        addedRefs.set(localId, refMap);
    }, null); // null origin → tracked by Y.UndoManager → undoable with Ctrl+Z

    return localId;
}

/**
 * Delete a reference from a reference mark, or the entire mark entry.
 *
 * - When `localId` is provided: removes that single reference from addedReferences.
 *   The mark entry itself is always preserved so that backend-written sibling keys
 *   (`claims`, `_meta`, `suggestedReferences`) are not lost when the last reference
 *   is removed.
 * - When `localId` is omitted/null: deletes the entire mark entry immediately
 *   (used when the ProseMirror node itself is deleted from the document).
 *
 * Fully undoable: Y.UndoManager (scoped to yReferenceMarks) will restore the
 * deleted Y.Map entry on Ctrl+Z, atomically with any associated mark text changes.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string}            referenceMarkId
 * @param {string}            [localId]  Optional — omit to delete the whole mark
 */
export function removeReferenceMark(ydoc, yReferenceMarks, referenceMarkId, localId) {
    if (!ydoc || !yReferenceMarks) throw new Error('[referencesYMap] ydoc and yReferenceMarks are required');
    if (!referenceMarkId) throw new Error('[referencesYMap] referenceMarkId is required');

    ydoc.transact(() => {
        if (!localId) {
            // No localId → delete the entire mark entry
            yReferenceMarks.delete(referenceMarkId);
            return;
        }
        const markMap = yReferenceMarks.get(referenceMarkId);
        if (!markMap) return;
        const addedRefs = markMap.get(ADDED_REFS_KEY);
        if (!addedRefs) return;
        addedRefs.delete(localId);
        // NOTE: Do NOT delete the whole mark entry when the last reference is removed.
        // The mark entry also holds backend-written keys (`claims`, `_meta`,
        // `suggestedReferences`) that are co-located on the same Y.Map node.
        // Deleting the entry would wipe out all extracted claims for this marker,
        // which is the root cause of claims disappearing when a reference is removed.
    }, null); // null origin → tracked by Y.UndoManager → undoable with Ctrl+Z
}

/**
 * Store an evidence score on a reference.
 * Called by the backend after claims verification; should NOT be called by the client.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string}            referenceMarkId
 * @param {string}            localId
 * @param {{ score: number, reason: string }|null} evidenceScore
 */
export function setEvidenceScore(ydoc, yReferenceMarks, referenceMarkId, localId, evidenceScore) {
    if (!ydoc || !yReferenceMarks) throw new Error('[referencesYMap] ydoc and yReferenceMarks are required');

    ydoc.transact(() => {
        const markMap = yReferenceMarks.get(referenceMarkId);
        if (!markMap) return;
        const addedRefs = markMap.get(ADDED_REFS_KEY);
        if (!addedRefs) return;
        const refMap = addedRefs.get(localId);
        if (!refMap) return;

        refMap.set('evidenceScore', evidenceScore ? JSON.stringify(evidenceScore) : null);
        refMap.set('updatedAt', Date.now());
    }, 'backend-claims-update'); // backend origin → NOT tracked by UndoManager
}

// ---------------------------------------------------------------------------
// Deserialisation
// ---------------------------------------------------------------------------

/**
 * Deserialise a single reference Y.Map into a plain JS object.
 *
 * @param {import('yjs').Map} refMap
 * @returns {Object}
 */
function deserialiseRef(refMap) {
    const obj = {};
    refMap.forEach((value, key) => {
        obj[key] = value;
    });

    // Deserialise JSON-encoded nested objects
    if (typeof obj.start === 'string') {
        try {
            obj.start = JSON.parse(obj.start);
        } catch {
            obj.start = null;
        }
    }
    if (typeof obj.end === 'string') {
        try {
            obj.end = JSON.parse(obj.end);
        } catch {
            obj.end = null;
        }
    }
    if (typeof obj.evidenceScore === 'string') {
        try {
            obj.evidenceScore = JSON.parse(obj.evidenceScore);
        } catch {
            obj.evidenceScore = null;
        }
    }

    // New flat structure: evidenceScore is a number, evidenceScoreReason is a separate string.
    // Normalize into the {score, reason} shape that all UI consumers expect.
    if (typeof obj.evidenceScore === 'number') {
        obj.evidenceScore = {
            score: obj.evidenceScore,
            reason: obj.evidenceScoreReason ?? null,
        };
    }

    return obj;
}

// ---------------------------------------------------------------------------
// Provenance filtering
// ---------------------------------------------------------------------------

/**
 * True when a reference was written by the forecast AI feature rather than
 * manually/drag-drop added by a user (MINSKY-16818 ticket 01/02).
 *
 * @param {Object} reference
 * @returns {boolean}
 */
export function isForecastReference(reference) {
    return reference?.source === 'forecast';
}

/**
 * Drop forecast-tagged references from a list, preserving order.
 * Used by claims-extraction analysis and reference-quality scoring so
 * AI-generated forecast citations aren't mis-evaluated as user-curated
 * references (MINSKY-16818 ticket 02). Docx export and print/report preview
 * intentionally do NOT use this — they keep rendering forecast citations
 * exactly like any other reference mark.
 *
 * @param {Object[]} references
 * @returns {Object[]}
 */
export function excludeForecastReferences(references) {
    if (!Array.isArray(references)) return [];
    return references.filter((ref) => !isForecastReference(ref));
}

// ---------------------------------------------------------------------------
// Public read API
// ---------------------------------------------------------------------------

/**
 * Return all added references for a given mark, sorted by createdAt.
 * Every entry present in the map is active — hard delete is used for removal.
 *
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string}            referenceMarkId
 * @returns {Object[]}
 */
export function getReferencesForMark(yReferenceMarks, referenceMarkId) {
    if (!yReferenceMarks || !referenceMarkId) return [];

    const markMap = yReferenceMarks.get(referenceMarkId);
    if (!markMap) return [];
    const addedRefs = markMap.get(ADDED_REFS_KEY);
    if (!addedRefs) return [];

    const result = [];
    addedRefs.forEach((refMap) => {
        if (refMap) result.push(deserialiseRef(refMap));
    });

    result.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
    return result;
}

/**
 * Return all added references grouped by referenceMarkId.
 * Used by useReferenceNumberStore to compute global citation numbers.
 *
 * @param {import('yjs').Map} yReferenceMarks
 * @returns {Map<string, Object[]>}  referenceMarkId → sorted references
 */
export function getAllReferences(yReferenceMarks) {
    const result = new Map();
    if (!yReferenceMarks) return result;

    yReferenceMarks.forEach((markMap, referenceMarkId) => {
        const addedRefs = markMap.get(ADDED_REFS_KEY);
        if (!addedRefs) return;

        const refs = [];
        addedRefs.forEach((refMap) => {
            if (refMap) refs.push(deserialiseRef(refMap));
        });
        refs.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
        if (refs.length > 0) {
            result.set(referenceMarkId, refs);
        }
    });

    return result;
}

// ---------------------------------------------------------------------------
// Observation
// ---------------------------------------------------------------------------

/**
 * Subscribe to any change anywhere inside Y.Map("referenceMarks").
 * Useful for useReferenceNumberStore which needs to rebuild citation numbers
 * whenever any reference anywhere in the document changes.
 *
 * @param {import('yjs').Map} yReferenceMarks
 * @param {Function}          callback  (events: Y.YEvent[]) => void
 * @returns {Function} Unsubscribe function
 */
export function observeReferences(yReferenceMarks, callback) {
    if (!yReferenceMarks || typeof callback !== 'function') return () => {
    };
    yReferenceMarks.observeDeep(callback);
    return () => yReferenceMarks.unobserveDeep(callback);
}

/**
 * Subscribe to changes within a **single** reference mark's subtree.
 * Fires on any change to addedReferences, claims, suggestedReferences, or _meta
 * under that mark. Used by ReferenceMarker per-mark hooks for efficient re-renders.
 *
 * @param {import('yjs').Map} yReferenceMarks
 * @param {string}            referenceMarkId
 * @param {Function}          callback  (events: Y.YEvent[]) => void
 * @returns {Function} Unsubscribe function
 */
export function observeReferencesForMark(yReferenceMarks, referenceMarkId, callback) {
    if (!yReferenceMarks || !referenceMarkId || typeof callback !== 'function') return () => {
    };

    const handler = (events) => {
        const relevant = events.some((event) =>
            // Only fire when the change is within this specific mark's subtree.
            // path[0] === referenceMarkId covers addedReferences, claims, _meta changes.
            // path.length === 0 means the root map itself changed (a mark was added or
            // removed); only include that if the changed key is this mark.
            (event.path.length === 0 && event.keysChanged && event.keysChanged.has(referenceMarkId))
            || (event.path.length > 0 && event.path[0] === referenceMarkId)
        );
        if (relevant) callback(events);
    };

    yReferenceMarks.observeDeep(handler);
    return () => yReferenceMarks.unobserveDeep(handler);
}
